import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOwnerComplex } from '../../../hooks/venue/useOwnerComplex';
import { theme } from '../../../theme';
import { Building2, Mail, MessageCircle } from 'lucide-react';

// Contact info - can be moved to config
const CONTACT_INFO = {
  email: 'hola@futbolero.app',
  whatsapp: '+5491155555555', // Replace with actual number
  whatsappDisplay: '+54 9 11 5555-5555',
};

const ComplexSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasComplex, loading, fetchComplex } = useOwnerComplex();

  useEffect(() => {
    fetchComplex();
  }, [fetchComplex]);

  useEffect(() => {
    if (!loading && hasComplex) {
      // Si ya tiene complejo, redirigir al dashboard
      navigate('/owner/dashboard');
    }
  }, [hasComplex, loading, navigate]);

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background,
    padding: '32px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '64px 48px',
    maxWidth: '600px',
    width: '100%',
    textAlign: 'center',
    border: `2px solid ${theme.colors.border}`,
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '100px',
    height: '100px',
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px auto',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '16px',
    fontFamily: theme.fonts.heading,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '40px',
    lineHeight: '1.6',
  };

  const contactSectionStyle: React.CSSProperties = {
    backgroundColor: 'rgba(0, 230, 118, 0.05)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  };

  const contactLabelStyle: React.CSSProperties = {
    fontSize: '14px',
    color: theme.colors.textSecondary,
    marginBottom: '16px',
    fontFamily: theme.fonts.body,
  };

  const contactItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '12px',
    color: theme.colors.text,
    fontSize: '16px',
    fontFamily: theme.fonts.body,
  };

  const whatsappButtonStyle: React.CSSProperties = {
    padding: '16px 32px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: '#25D366', // WhatsApp green
    color: '#fff',
    textDecoration: 'none',
  };

  const loadingStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Hola! Quiero activar mi cuenta de propietario en Futbolero para gestionar mi complejo deportivo.'
    );
    window.open(
      `https://wa.me/${CONTACT_INFO.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Verificando...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={iconContainerStyle}>
          <Building2 size={48} color={theme.colors.primary} />
        </div>

        <h1 style={titleStyle}>Tu cuenta esta pendiente de activacion</h1>

        <p style={subtitleStyle}>
          Contactanos para configurar tu complejo deportivo en Futbolero.
          Te ayudaremos a dar de alta tu club y empezar a recibir reservas.
        </p>

        <div style={contactSectionStyle}>
          <p style={contactLabelStyle}>Contactanos por:</p>

          <div style={contactItemStyle}>
            <Mail size={20} color={theme.colors.primary} />
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              style={{ color: theme.colors.primary, textDecoration: 'none' }}
            >
              {CONTACT_INFO.email}
            </a>
          </div>

          <div style={contactItemStyle}>
            <MessageCircle size={20} color="#25D366" />
            <span>{CONTACT_INFO.whatsappDisplay}</span>
          </div>
        </div>

        <button
          style={whatsappButtonStyle}
          onClick={handleWhatsAppClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#128C7E';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#25D366';
          }}
        >
          <MessageCircle size={20} />
          Escribinos por WhatsApp
        </button>
      </div>
    </div>
  );
};

export default ComplexSetupPage;
